import styled, { css } from 'styled-components';
import { Indicator, Tag, Center } from 'legacyComponents/styled/lib';
import {
  media as oldMedia,
  truncateWidth,
} from 'legacyComponents/styled/styleUtils';
import { media, customMedia } from 'styles/utils';
import { Banner, Image as ImageComp, Button } from 'components';
import { Link } from 'core/routing';

export const Title = styled.h3`
  font-size: ${props => props.theme.font.size.medium};
  margin-bottom: ${props => props.theme.spacing.small};
  transition: color 100ms ease-in-out;
  color: ${props => props.theme.color.wfBlack};
  ${truncateWidth('100%')};
  &:hover {
    text-decoration: underline;
  }
`;

export const CardFooter = styled.nav`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${props => props.theme.color.wfGrey200};
  max-height: ${props => props.maxHeight || '4.125rem'};
`;

export const CardFooterList = styled.ul`
  width: 100%;
  display: flex;
  justify-content: ${props => props.justifyContent || 'space-around'};
  align-items: center;
  color: ${props => props.theme.color.wfGrey600};
`;

export const CardFooterItem = styled.li`
  font-size: ${props => props.theme.font.size.default};
  padding: ${props => props.theme.spacing.medium} 0;
  &:only-child {
    margin-right: auto;
    padding: ${props => props.theme.spacing.medium};
  }
  svg {
    margin-right: ${props =>
      props.noMargin ? '0' : props.theme.spacing.small};
    margin-top: -2px;
  }
  > a {
    color: currentColor;
  }
`;

export const CardFooterButton = styled(Button)`
  color: ${props => props.theme.color.wfBlack};
  padding: ${props => props.theme.spacing.medium};
  border-left: 1px solid ${props => props.theme.color.wfGrey200};
  &:hover {
    color: ${props => props.theme.color.wfWhite};
    background-color: ${props => props.theme.color.wfBlue};
    border-left: 1px solid ${props => props.theme.color.wfGrey200};
  }
`;

// Base Card (Project, Collection, Machine)
export const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  background-color: ${props => props.theme.color.wfWhite};
  border: 1px solid ${props => props.theme.color.wfGrey200};
  z-index: 1;
  overflow: hidden;

  &:hover {
    box-shadow: 0 1px 1px rgba(44, 45, 48, 0.07);
    div > a > img {
      opacity: 0.85;
    }
    ${Title} {
      color: ${props => props.theme.color.wfBlue};
    }
    ${CardFooterButton} {
      color: ${props => props.theme.color.wfWhite};
      background-color: ${props => props.theme.color.wfBlue};
    }
    ${CardFooterList} {
      color: ${props => props.theme.color.wfBlack};
    }
    > div:nth-of-type(1) > div:nth-of-type(1) {
      opacity: 1;
      transform: translate(10px);
    }
  }
`;

const ImageMQs = `
  ${oldMedia.l` height: 230px; `};
  ${oldMedia.m` height: 250px; `};
  ${oldMedia.s` height: 200px; `};
  ${oldMedia.xs`height: auto; `};
`;

export const Image = styled.img`
  width: 100%;
  background-color: lightgrey;
  transition: opacity 100ms ease-in-out;
  border-bottom: 1px solid ${props => props.theme.color.wfGrey100};
  &:hover {
    cursor: pointer;
  }
  ${ImageMQs}
`;

export const ImageIndicator = styled(Indicator)`
  width: 100%;
  ${ImageMQs}
`;

export const TagsWrapper = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.default};
  right: ${props => props.withPrivate && '14%'};
  left: 0;
  opacity: 0;
  transition: opacity 350ms ease-in-out, transform 350ms ease-in-out;
`;

export const CardTag = styled(Tag)`
  margin-left: 0px;
  margin-right: 10px;
  display: inline-block;
  ${oldMedia.s`
    display: none;
  `};
`;

export const ThinCardTag = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  border-radius: ${props => props.theme.borderRadius.large};
  background-color: ${props => props.theme.color.wfGrey200};
  padding: 0.25em 0.5em 0.3em;
  font-size: 10px;
  color: ${props => props.theme.color.wfBlack};
  transition: color 100ms ease-in-out;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.theme.color.wfGrey300};
  }
  svg {
    min-width: 1rem;
  }

  ${oldMedia.s`
    display: none;
  `};
`;

export const InfoWrapper = styled.div`
  width: 100%;
  position: relative;
  padding: ${props =>
    `${props.theme.spacing.medium} ${props.theme.spacing.large} ${props.theme.spacing.medium}`};
`;

export const Count = styled.span`
  color: ${props => props.theme.color.wfGrey500};
`;

export const Description = styled.p`
  font-size: ${props => props.theme.font.size.default};
  ${truncateWidth('100%')};
`;

export const TypeTag = styled.div`
  position: absolute;
  z-index: 5;
  width: 100%;
  height: 29px;
  bottom: 0;
  background-color: ${props =>
    props.isPublic ? props.theme.color.wfBlue : props.theme.color.wfBlack};
  color: ${props => props.theme.color.wfWhite};
  font-size: ${props => props.theme.font.size.small};
  text-align: center;
  padding: ${props => props.theme.spacing.xsmall} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  > svg {
    margin-right: ${props => props.theme.spacing.small};
  }
`;

// Full Width Card (Post, Issue)
export const PostCard = styled.article`
  padding: ${props => props.theme.spacing.default};
  display: flex;
  border: 1px solid ${props => props.theme.color.wfGrey300};
  border-left: ${props => props.colorCard && `4px solid ${props.colorCard}`};
  margin-bottom: ${props => props.theme.spacing.default};
  position: relative;
  &:hover {
    box-shadow: 0 1px 1px rgba(44, 45, 48, 0.07);
    h3 {
      color: ${props => props.theme.color.primary};
      > span {
        color: currentColor;
      }
    }
  }
  /** Media queries */
  ${media.large`
    padding: ${props => props.theme.spacing.medium};
  `}
`;

export const TextWrapper = styled.div`
  margin-left: ${props => props.theme.spacing.medium};
  margin-right: auto;
  /** Media queries */
  ${media.large`
    flex-basis: 75%;
  `}
`;

export const PostCardActions = styled.nav`
  > button,
  > a {
    margin-left: ${props => props.theme.spacing.default};
  }
  /** Media queries */
  ${customMedia.lessThan('large')`
      display: none;
  `}
`;

export const pseudoCardStyles = css`
  content: '|';
  display: inline-block;
  margin: 0 ${props => props.theme.spacing.small};
  color: ${props => props.theme.color.wfGrey700};
`;

export const FullWidthTitle = styled.h3`
  font-size: ${props => props.theme.font.size.default};
  color: ${props => props.theme.color.wfBlack};
  line-height: 1.45;
  transition: color 100ms ease-in-out;
  display: inline-block;
  margin-right: ${props => props.theme.spacing.default};
  vertical-align: middle;
  ${props =>
    props.truncate ? `${truncateWidth('800px')}; margin-right: 0px;` : ''}
  &:hover {
    text-decoration: underline;
    > span {
      color: ${props => props.theme.color.primary};
    }
  }
  ${oldMedia.l`
    font-size: 0.85em;
    ${props =>
      props.truncate ? `${truncateWidth('700px')}; margin-right: 5px;` : ''}
  `};
`;

export const FullWidthDescription = styled.p`
  font-size: ${props => props.theme.font.size.small};
`;

// SmallCard (Contributor, Label)
export const SmallCard = styled.div`
  background-color: ${props => props.theme.color.wfWhite};
  border: 1px solid ${props => props.theme.color.wfGrey300};
  display: inline-block;
  padding: ${props => `${props.theme.spacing.default}`};
  &:hover {
    box-shadow: 0 1px 1px rgba(44, 45, 48, 0.07);
  }
`;

export const SmallCardTextWrapper = styled(TextWrapper)`
  margin-left: 10px;
  display: inline-block;
`;

export const SmallCardTitle = styled(FullWidthTitle)`
  float: none;
`;

export const SmallCardDescription = styled(FullWidthDescription)`
  clear: none;
`;

// MediumCard (Story)
export const MediumCard = styled.div`
  position: relative;
  border: 1px solid ${props => props.theme.color.wfGrey300};
  display: inline-block;
  vertical-align: top;
  max-width: 92vw;
  &:hover {
    box-shadow: 0 1px 1px rgba(44, 45, 48, 0.07);
    > div:nth-child(1) {
      opacity: 0.9;
    }
    > div > a > h3 {
      color: ${props => props.theme.color.primary};
    }
    ${CardFooterButton} {
      color: ${props => props.theme.color.wfWhite};
      background-color: ${props => props.theme.color.wfBlue};
    }
    ${CardFooterList} {
      color: ${props => props.theme.color.wfBlack};
    }
    ${CardFooterItem} {
      svg,
      span {
        color: ${props => props.theme.color.wfBlack};
      }
    }
  }
`;

export const MediumCardImg = styled(ImageComp)`
  width: 100%;
  height: 265px;
  border-bottom: 1px solid ${props => props.theme.color.wfGrey200};
  transition: opacity 100ms ease-in-out;
  object-fit: cover;
  &:hover {
    cursor: pointer;
  }
  ${oldMedia.l`
    height: 238px;
  `};
  ${oldMedia.m`
    height: 347px;
  `};
  ${oldMedia.s`
    height: 275px;
  `};
  ${oldMedia.xs`
    height: 200px;
  `};
`;

export const MediumInfoWrapper = styled.div`
  padding: ${props =>
    `${props.theme.spacing.default} ${props.theme.spacing.default} 0`};
  height: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Media queries */
  ${media.large`
  padding: ${props =>
    `${props.theme.spacing.default} ${props.theme.spacing.medium} 0`};
  `}
`;

export const MediumStatsWrapper = styled.div`
  padding: ${props =>
    `${props.theme.spacing.small} ${props.theme.spacing.medium}`};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StackedCard = styled.div`
  width: 376px;
  box-shadow: rgba(75, 75, 75, 0.2) 0 1px 1px;
  height: 20px;
  position: absolute;
  bottom: 24px;
  left: 5px;
  z-index: 0;
  ${oldMedia.l`
      width: 336px;
    `};
  ${oldMedia.m`
      width: 365px;
    `};
  ${oldMedia.s`
      width: 290px;
    `};
`;

export const DiscussLink = styled(Link)`
  color: ${props => props.theme.color.wfGrey500};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${props => props.theme.color.primary};
  }
`;

export const GreyBar = styled.div`
  width: 100%;
  height: 40px;
  background-color: ${props => props.theme.color.wfWhite};
  border-bottom: 1px dashed ${props => props.theme.color.wfGrey200};
`;

export const AvatarWrapper = styled(Center)`
  margin-top: -30px;
  overflow: hidden;
`;

export const BannerStyled = styled(Banner)`
  position: absolute;
  z-index: 5;
  right: ${props => props.theme.spacing.small};
  margin: ${props => props.theme.spacing.small} 0;
`;
